import { isEqual, pick } from 'es-toolkit';
import React, { FunctionComponent, useCallback, useState } from 'react';
import { Box, Checkbox, Divider, FormControlLabel, Typography } from '@mui/material';

import {
  FeaturePatternTrussSettings,
  isFeaturePatternTruss,
  isProfileTruss,
  MaterialSelection,
  MaterialSettings,
  MaterialSettingsPair,
  ModelCategory,
  uniqueProfileMaterialSettingsProperties,
} from '@paragon/contract-library';
import { hasCanUsePlatesPermission } from '@paragon/login-tools';
import { getLoggedInUsersOrganizationMaterialSet, MaterialListSelect } from '@paragon/material-lists';

import { closeSettingsPanel } from '../../../../../store/actions/appActions';
import { updateActiveFeaturePatternTrussSettings } from '../../../../../store/actions/editorActions';
import { getUniqueLumbersInLumberList } from '../../../../../store/selectors/getUniqueLumbersInLumberList';
import { isBlockedFromChangingMaterialSelectionBasedOnJobPermissions } from '../../../../../store/selectors/jobContextSelectors';
import {
  isComponentModel,
  isSettingsPanelLayoutModel,
  SettingsPanelSelectedModel,
} from '../../../../../store/selectors/settingsPanelSelectors';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { SettingsControlsPresetToolbar } from '../preset-toolbar/SettingsControlsPresetToolbar';
import SettingsPanelBase from '../SettingsPanelBase';
import SettingsSaveButton, { SaveAction } from '../SettingsSaveButton';
import ComponentThicknessProperty from './ComponentThicknessProperty';
import MaterialSettingsLumbers from './MaterialSettingsLumbers';
import { MaterialSettingsToolbar } from './MaterialSettingsToolbar';
import { restrictMaterialSettingsLumbers } from './materialSettingsValidation';

interface Props {
  readonly selectedModel: NonNullable<SettingsPanelSelectedModel>;
  readonly showHeader?: boolean /** default: true */;
  readonly showPresetControls?: boolean /** default: true */;
  readonly featurePatternTrussSettings?: FeaturePatternTrussSettings;
  readonly materialSettings: MaterialSettingsPair;
  readonly materialSelection: MaterialSelection;
  readonly saveMaterialSettings: (materialSettings: MaterialSettingsPair, saveAction: SaveAction) => void;
  readonly saveMaterialSelection: (materialSelection: MaterialSelection, saveAction: SaveAction) => void;
}

export const MaterialSettingsControls: FunctionComponent<Props> = (props) => {
  const dispatch = useAppDispatch();
  const canUsePlates = useAppSelector(hasCanUsePlatesPermission);

  const [updatedFeaturePatternTrussSettings, setUpdatedFeaturePatternTrussSettings] =
    useState<FeaturePatternTrussSettings | null>(null);
  const [updatedFeaturePatternMaterialSettings, setUpdatedFeaturePatternMaterialSettings] =
    useState<MaterialSettings | null>(null);
  const [updatedProfileMaterialSettings, setUpdatedProfileMaterialSettings] = useState<MaterialSettings | null>(null);

  const [updatedLumberListGuid, setUpdatedLumberListGuid] = useState<string | null>(null);
  const [updatedPlateListGuid, setUpdatedPlateListGuid] = useState<string | null>(null);
  const [updatedHangerListGuid, setUpdatedHangerListGuid] = useState<string | null>(null);

  const componentIsFeaturePatternTruss =
    isComponentModel(props.selectedModel) && isFeaturePatternTruss(props.selectedModel.model.component);
  const componentIsProfileTruss =
    isComponentModel(props.selectedModel) && isProfileTruss(props.selectedModel.model.component);
  const isLayoutModel =
    isSettingsPanelLayoutModel(props.selectedModel) &&
    props.selectedModel.model.category !== ModelCategory.ComponentDesign;

  const featurePatternTrussSettings = updatedFeaturePatternTrussSettings ?? props.featurePatternTrussSettings;
  const featurePatternMaterialSettings =
    updatedFeaturePatternMaterialSettings ?? props.materialSettings.featurePatternMaterialSettings;
  const profileMaterialSettings = updatedProfileMaterialSettings ?? props.materialSettings.profileMaterialSettings;

  const materialSettingsAreEquivalent = isEqual(featurePatternMaterialSettings, profileMaterialSettings);
  const [shareCommonMaterialSettingsFields, setShareCommonMaterialSettingsFields] =
    useState<boolean>(materialSettingsAreEquivalent);

  const toggleSharedMaterialSettings = (share: boolean) => {
    setShareCommonMaterialSettingsFields(share);

    if (share && profileMaterialSettings && featurePatternMaterialSettings) {
      const sharingCausesChanges = !isEqual(featurePatternMaterialSettings, profileMaterialSettings);
      if (sharingCausesChanges) {
        // in case of differences, prefer the feature pattern settings, but retain unique material settings properties
        const mergedSettings: MaterialSettings = {
          ...featurePatternMaterialSettings,
          ...pick(profileMaterialSettings, uniqueProfileMaterialSettingsProperties),
        };

        setUpdatedFeaturePatternMaterialSettings(mergedSettings);
        setUpdatedProfileMaterialSettings(mergedSettings);
      }
    }
  };

  const changeFeaturePatternOrBothMaterialSettings = (settings: MaterialSettings) => {
    setUpdatedFeaturePatternMaterialSettings(settings);

    if (shareCommonMaterialSettingsFields && profileMaterialSettings) {
      setUpdatedProfileMaterialSettings(settings);
    }
  };

  const changeProfileOrBothMaterialSettings = (settings: MaterialSettings) => {
    setUpdatedProfileMaterialSettings(settings);

    if (shareCommonMaterialSettingsFields && featurePatternMaterialSettings) {
      setUpdatedFeaturePatternMaterialSettings(settings);
    }
  };

  const settingsChanged = !!(
    updatedFeaturePatternTrussSettings ||
    updatedFeaturePatternMaterialSettings ||
    updatedProfileMaterialSettings ||
    updatedLumberListGuid ||
    updatedPlateListGuid ||
    updatedHangerListGuid
  );

  const lumberListGuid = updatedLumberListGuid ?? props.materialSelection.lumberListGuid;
  const plateListGuid = updatedPlateListGuid ?? props.materialSelection.plateListGuid;
  const hangerListGuid = updatedHangerListGuid ?? props.materialSelection.hangerListGuid;

  const materialSet = useAppSelector(getLoggedInUsersOrganizationMaterialSet);
  const lumberListLumbers = getUniqueLumbersInLumberList(materialSet, lumberListGuid);
  const materialListChangingDisabled = useAppSelector(isBlockedFromChangingMaterialSelectionBasedOnJobPermissions);

  const updateAndRestrictLumbers = useCallback(
    (newLumberListGuid: string, newFeaturePatternTrussThickness?: number | undefined) => {
      if (lumberListGuid !== newLumberListGuid) setUpdatedLumberListGuid(newLumberListGuid);
      if (
        newFeaturePatternTrussThickness != null &&
        featurePatternTrussSettings!.thickness !== newFeaturePatternTrussThickness
      ) {
        setUpdatedFeaturePatternTrussSettings({
          ...featurePatternTrussSettings!,
          thickness: newFeaturePatternTrussThickness,
        });
      }

      const newLumberListLumbers = getUniqueLumbersInLumberList(materialSet, newLumberListGuid);

      if (componentIsFeaturePatternTruss || isLayoutModel) {
        const newFeaturePatternMaterialSettings = restrictMaterialSettingsLumbers(
          featurePatternMaterialSettings!,
          newLumberListLumbers,
          newFeaturePatternTrussThickness ?? featurePatternTrussSettings?.thickness,
        );

        if (newFeaturePatternMaterialSettings !== featurePatternMaterialSettings) {
          setUpdatedFeaturePatternMaterialSettings(newFeaturePatternMaterialSettings);
        }
      }

      if (componentIsProfileTruss || isLayoutModel) {
        const newProfileMaterialSettings = restrictMaterialSettingsLumbers(
          profileMaterialSettings!,
          newLumberListLumbers,
          1.5, // Hardcoded for now
        );

        if (newProfileMaterialSettings !== profileMaterialSettings) {
          setUpdatedProfileMaterialSettings(newProfileMaterialSettings);
        }
      }
    },
    [
      featurePatternTrussSettings,
      lumberListGuid,
      componentIsFeaturePatternTruss,
      componentIsProfileTruss,
      featurePatternMaterialSettings,
      isLayoutModel,
      materialSet,
      profileMaterialSettings,
    ],
  );

  function onSave(saveAction: SaveAction) {
    if (updatedLumberListGuid || updatedPlateListGuid || updatedHangerListGuid) {
      props.saveMaterialSelection({ lumberListGuid, plateListGuid, hangerListGuid }, saveAction);
    }

    const materialSettingsUpdated = !!(updatedFeaturePatternMaterialSettings || updatedProfileMaterialSettings);

    if (updatedFeaturePatternTrussSettings) {
      dispatch(
        // Skip updating the insertion truss when there is a thickness change and material settings changes. The
        // insertion truss will be updated below on save of the material settings.
        updateActiveFeaturePatternTrussSettings(updatedFeaturePatternTrussSettings, {
          skipUpdateInsertionTruss: materialSettingsUpdated,
        }),
      );
    }

    if (materialSettingsUpdated) {
      const updatedMaterialSettings: MaterialSettingsPair = {
        featurePatternMaterialSettings,
        profileMaterialSettings,
      };
      props.saveMaterialSettings(updatedMaterialSettings, saveAction);
    }

    dispatch(closeSettingsPanel());
  }

  const showFeaturePatternMaterialSettings = componentIsFeaturePatternTruss || isLayoutModel;
  const showProfileMaterialSettings = componentIsProfileTruss || isLayoutModel;
  const showMergedMaterialSettings =
    showFeaturePatternMaterialSettings && showProfileMaterialSettings && shareCommonMaterialSettingsFields;

  return (
    <SettingsPanelBase
      title={props.showHeader !== false ? 'Material Settings' : undefined}
      saveButtons={
        <SettingsSaveButton
          userVisibleModelType={props.selectedModel.type === 'component' ? 'component' : props.selectedModel.model.type}
          enableSaving={settingsChanged}
          onSave={onSave}
        />
      }
    >
      <MaterialSettingsToolbar selectedModelGuid={props.selectedModel.model.guid} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', m: 2 }}>
        <MaterialListSelect
          sx={{ m: 1 }}
          type="lumber"
          disabled={materialListChangingDisabled}
          selectedGuid={lumberListGuid}
          onEdit={updateAndRestrictLumbers}
        />
        {canUsePlates && (
          <MaterialListSelect
            sx={{ m: 1 }}
            type="plate"
            disabled={materialListChangingDisabled}
            selectedGuid={plateListGuid}
            onEdit={setUpdatedPlateListGuid}
          />
        )}
        <MaterialListSelect
          sx={{ m: 1 }}
          type="hanger"
          disabled={materialListChangingDisabled}
          selectedGuid={hangerListGuid}
          onEdit={setUpdatedHangerListGuid}
        />
      </Box>

      {componentIsFeaturePatternTruss && featurePatternTrussSettings && (
        <ComponentThicknessProperty
          thickness={featurePatternTrussSettings.thickness}
          setFeaturePatternTrussThickness={(thickness) => updateAndRestrictLumbers(lumberListGuid, thickness)}
        />
      )}

      <Divider sx={{ marginBottom: 2 }} />

      {showMergedMaterialSettings && props.showPresetControls !== false && featurePatternMaterialSettings && (
        <SettingsControlsPresetToolbar
          settingsType="materialSettings"
          settings={featurePatternMaterialSettings}
          materialSelection={{
            lumberListGuid,
            plateListGuid,
            hangerListGuid,
          }}
          onOverwriteSettingsFromPreset={(settings, { lumberListGuid, plateListGuid, hangerListGuid }) => {
            if (lumberListGuid) setUpdatedLumberListGuid(lumberListGuid);
            if (plateListGuid) setUpdatedPlateListGuid(plateListGuid);
            if (hangerListGuid) setUpdatedHangerListGuid(hangerListGuid);

            changeFeaturePatternOrBothMaterialSettings(settings);
          }}
        />
      )}

      {isLayoutModel && <Typography sx={{ textAlign: 'center' }}>Feature Pattern Material Settings</Typography>}
      {showFeaturePatternMaterialSettings && featurePatternMaterialSettings && (
        <>
          {props.showPresetControls !== false && !showMergedMaterialSettings && (
            <SettingsControlsPresetToolbar
              settingsType="materialSettings"
              settings={featurePatternMaterialSettings}
              materialSelection={{
                lumberListGuid,
                plateListGuid,
                hangerListGuid,
              }}
              onOverwriteSettingsFromPreset={(settings, { lumberListGuid, plateListGuid, hangerListGuid }) => {
                if (lumberListGuid) setUpdatedLumberListGuid(lumberListGuid);
                if (plateListGuid) setUpdatedPlateListGuid(plateListGuid);
                if (hangerListGuid) setUpdatedHangerListGuid(hangerListGuid);

                changeFeaturePatternOrBothMaterialSettings(settings);
              }}
            />
          )}

          <MaterialSettingsLumbers
            mode="featurePattern"
            settings={featurePatternMaterialSettings}
            onSettingsChange={changeFeaturePatternOrBothMaterialSettings}
            inventoryLumbers={lumberListLumbers}
            componentThickness={featurePatternTrussSettings?.thickness}
          />
        </>
      )}

      {isLayoutModel && <Typography sx={{ textAlign: 'center', marginTop: 3 }}>Profile Material Settings</Typography>}

      {showFeaturePatternMaterialSettings && showProfileMaterialSettings && (
        <Box sx={{ display: 'flex' }}>
          <FormControlLabel
            sx={{ m: 'auto' }}
            control={
              <Checkbox
                checked={shareCommonMaterialSettingsFields}
                onChange={(_, checked) => toggleSharedMaterialSettings(checked)}
              />
            }
            label="Use same material settings for all truss input methods"
          />
        </Box>
      )}

      {showProfileMaterialSettings && profileMaterialSettings && (
        <>
          {props.showPresetControls !== false && !showMergedMaterialSettings && (
            <SettingsControlsPresetToolbar
              settingsType="materialSettings"
              settings={profileMaterialSettings}
              materialSelection={{
                lumberListGuid,
                plateListGuid,
                hangerListGuid,
              }}
              onOverwriteSettingsFromPreset={(settings, { lumberListGuid, plateListGuid, hangerListGuid }) => {
                if (lumberListGuid) setUpdatedLumberListGuid(lumberListGuid);
                if (plateListGuid) setUpdatedPlateListGuid(plateListGuid);
                if (hangerListGuid) setUpdatedHangerListGuid(hangerListGuid);

                setUpdatedProfileMaterialSettings(settings);
              }}
            />
          )}

          <MaterialSettingsLumbers
            mode="profile"
            settings={profileMaterialSettings}
            onSettingsChange={changeProfileOrBothMaterialSettings}
            inventoryLumbers={lumberListLumbers}
            shareCommonMaterialSettingsFields={showMergedMaterialSettings}
          />
        </>
      )}
    </SettingsPanelBase>
  );
};
